'use client';

import {
  ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import NiceModal from '@ebay/nice-modal-react';
import { MicrophoneIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { Button, bjToast } from '@components/common';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import { useAutoResizeTextarea } from '@hooks/useAutoResizeTextarea';
import { BestieVoiceModal } from '@components/features/modals/gemini-voice';
import { useBestieChatStateOptional } from '@components/contexts/BestieChatStateContext';
import {
  ACCEPTED_ATTACHMENT_EXTENSIONS,
  ChatAttachment,
  readFileAsAttachment,
  validateAttachmentFile,
} from '@components/features/candidate-prompt-bar/chatAttachment';
import { ChatAttachmentChip } from '@components/features/candidate-prompt-bar/ChatAttachmentChip';
import { ChatAttachMenu } from '@components/features/candidate-prompt-bar/ChatAttachMenu';
import { loginRedirect } from './usePromptBarActions';

interface ChatBranchProps {
  /**
   * Receives the staged attachment as a second argument when the composer has one. Callers that
   * never enable `allowAttachments` can keep a one-argument handler.
   */
  onSubmit: (text: string, attachment?: ChatAttachment | null) => Promise<void>;
  leftSlot?: ReactNode;
  /** Override the empty-input mic action (default: open the Bestie voice modal). */
  onMic?: () => void;
  /** Extra control rendered after the mic/send button (e.g. a video-answer button). */
  rightSlot?: ReactNode;
  /** Override the default "What are your career plans?" placeholder/aria-label. */
  placeholder?: string;
  /**
   * Turn the `+` into an attach button. Off by default: only the Bestie chat has an agent that can
   * act on a file, and the other callers of this component keep whatever their `leftSlot` holds.
   *
   * Shown to everyone, signed in or not — hiding it would leave a guest wondering whether
   * attaching is possible at all. The action is what needs an account: a signed-out visitor is
   * sent to login instead of the picker.
   */
  allowAttachments?: boolean;
}

export const ChatBranch = ({
  onSubmit, leftSlot, onMic, rightSlot, placeholder, allowAttachments = false,
}: ChatBranchProps) => {
  const { t } = useTranslation('common');
  // Client session, not the page's SSR-computed prop: that one is fixed for the life of the mount,
  // so signing in from the modal below would otherwise leave the menu gated until a full reload.
  const { status } = useSession();
  const isSignedIn = status === 'authenticated';
  const router = useRouter();
  const pathname = usePathname();
  const composer = useChatComposer({ onSubmit });
  const [isEditing, setIsEditing] = useState(false);
  // useChatComposer clears the input immediately (before awaiting onSubmit), so hasText alone
  // would flip the button back to the empty/mic state while the save is still in flight — track
  // the pending save separately so the button shows a spinner instead until it actually resolves.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const chatState = useBestieChatStateOptional();

  // Open the input ready to type when the user arrived here via the prompt-bar
  // "coaching" action (which set a one-shot request on the shared MC state).
  // Read-and-clear once on mount; the focus effect below then focuses it.
  const consumeChatFocusRequest = chatState?.consumeChatFocusRequest;
  useEffect(() => {
    if (consumeChatFocusRequest?.()) setIsEditing(true);
  }, [consumeChatFocusRequest]);

  const hasText = composer.input.trim().length > 0;
  // The mic/send button and the send guard both key off "is there something to send" — an
  // attachment on its own counts, and needs no text beside it: the agents service prepends the
  // archived-file link to the turn, so the model sees the file either way.
  // Declared above handleSend because it is in that callback's dependency array, which is
  // evaluated during render.
  const canSend = hasText || composer.attachment !== null;

  const handleFilePicked = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice in a row still fires onChange.
    e.target.value = '';
    if (!file) return;

    // Reuses the strings the existing CV upload already shows for the same two rejections
    // (`UploadFileCv.tsx` uses `error.maxsize` for an identical 10MB cap), so the chat and the
    // profile upload it feeds do not disagree about the same file. The type message is the
    // format-agnostic one: the menu offers photos as well as documents, so naming only PDF and
    // Word would misdescribe the rejection to anyone who picked an unsupported image.
    const verdict = validateAttachmentFile(file);
    if (verdict === 'type') {
      bjToast.error(t('dropzone.invalid_file_type'));
      return;
    }
    if (verdict === 'size') {
      bjToast.error(t('error.maxsize'));
      return;
    }

    try {
      composer.setAttachment(await readFileAsAttachment(file));
    } catch {
      bjToast.error(t('global.something.went.wrong'));
    }
  }, [composer, t]);

  // Signed-out visitors see the button but cannot attach: whatever consumes a file acts on the
  // signed-in user's own profile, and an upload with no owner behind it has nowhere to land. They
  // go to login with a path back here, the same way this bar's other gated actions do.
  const handleAttachClick = useCallback(() => {
    if (!isSignedIn) {
      // `usePathname` is typed nullable; with no path to return to, plain /login is still correct.
      router.push(pathname ? loginRedirect(pathname) : '/login');
      return;
    }
    fileInputRef.current?.click();
  }, [isSignedIn, router, pathname]);

  const handleSend = useCallback(async () => {
    if (!canSend || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await composer.submit();
    } finally {
      setIsSubmitting(false);
    }
    // Keep the textarea mounted and focused so the user can keep chatting
    // without re-clicking the input after every message.
    composer.textareaRef.current?.focus();
  }, [composer, isSubmitting, canSend]);

  const { resize } = useAutoResizeTextarea({ ref: composer.textareaRef, value: composer.input, maxRows: 3 });

  useEffect(() => {
    if (!isEditing) return;
    const el = composer.textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    resize();
  }, [isEditing, resize, composer.textareaRef]);

  // No collapse-on-empty. Deleting your own text back to nothing used to fold the composer shut
  // mid-edit, which unmounts the textarea and takes the caret with it — so the next keystroke went
  // nowhere and the field had to be clicked again. Leaving the field still collapses it, via the
  // textarea's `onBlur` below, which is the case the collapsed view actually exists for.

  // The placeholder is audience-specific: asking someone posting a job about their career plans reads as
  // nonsense. The Bestie chat supplies an employer-aware key; an explicit `placeholder` prop still wins,
  // and the other `ChatBranch` callers (stories answer bar, interview answer bar) have no chat state and
  // keep the candidate copy.
  const inputPlaceholder = placeholder ?? t(chatState?.inputPlaceholderKey ?? 'homepage.chat.input.placeholder');

  // One picker for both kinds. The `accept` list steers the OS dialog; the media type derived
  // from the chosen file is what decides how the service handles it, so there is nothing for the
  // user to choose between here.
  const attachButton = allowAttachments && (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={ACCEPTED_ATTACHMENT_EXTENSIONS.join(',')}
        onChange={(e) => { void handleFilePicked(e); }}
      />
      <ChatAttachMenu onPick={handleAttachClick} />
    </>
  );

  const attachmentChip = composer.attachment && (
    <div className="flex w-full px-2 pb-1">
      <ChatAttachmentChip filename={composer.attachment.filename} onRemove={composer.clearAttachment} />
    </div>
  );

  const actionButton = (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      isLoading={isSubmitting}
      aria-label={canSend ? t('global.label.send', 'Send') : t('global.label.voice', 'Voice')}
      className="ml-auto h-9! w-9! shrink-0 rounded-lg! border-none! bg-secondary p-0! transition-transform hover:scale-110 hover:bg-secondary! lg:h-10! lg:w-10!"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (canSend) void handleSend();
        else if (onMic) onMic();
        else void NiceModal.show(BestieVoiceModal);
      }}
    >
      {isSubmitting ? null : (canSend
        ? <PaperAirplaneIcon className="size-5 text-white" />
        : <MicrophoneIcon className="size-5 text-white" />)}
    </Button>
  );

  if (!isEditing) {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
        className="flex w-full flex-col"
      >
        {attachmentChip}
        <div className="flex w-full items-center gap-2">
          {leftSlot}
          {attachButton}
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            aria-label={inputPlaceholder}
            className="flex min-w-0 flex-1 px-2 py-1 text-left text-xs leading-normal md:text-sm"
          >
            {composer.input
              ? <span className="block w-full truncate text-ink">{composer.input}</span>
              : <span className="block w-full truncate text-ink/60">{inputPlaceholder}</span>}
          </button>
          {actionButton}
          {!canSend && !isSubmitting && rightSlot}
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); void handleSend(); }}
      className="flex w-full flex-col gap-1"
    >
      {attachmentChip}
      <textarea
        ref={composer.textareaRef}
        rows={1}
        value={composer.input}
        onChange={composer.onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
            return;
          }
          composer.onKeyDown(e);
        }}
        onBlur={() => setIsEditing(false)}
        aria-label={inputPlaceholder}
        className="block w-full resize-none border-none bg-transparent px-2 py-1 text-base leading-normal text-ink shadow-none outline-none focus:ring-0 focus:ring-offset-0 md:text-sm"
      />

      <div className="flex w-full items-center gap-2">
        {leftSlot}
        {attachButton}
        {actionButton}
        {!canSend && !isSubmitting && rightSlot}
      </div>
    </form>
  );
};
